import { useEffect, useMemo, useRef, useState } from "react";
import {
  ArrowLeft,
  Save,
  Mail,
  Sparkles,
  Loader2,
  Mic,
  MicOff,
  RotateCcw,
  CheckCircle2,
  Send,
  Wand2,
} from "lucide-react";
import { toast } from "sonner";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../ui/tabs";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import FieldEditor from "./FieldEditor";
import AudioPlayer from "./AudioPlayer";
import VoiceRoutingHint from "./VoiceRoutingHint";
import useVoiceCapture from "../../hooks/useSpeechRecognition";
import {
  extractClinicalNotes,
  submitPlan,
  transcribeAudio,
  getTranscribeStatus,
} from "../../lib/api";
import {
  EVAL_SLOTS,
  DAILY_SLOTS,
  getTherapyConfig,
  readSlot,
} from "../../lib/therapyTypes";
import { routeTranscriptToSlots } from "../../lib/voiceRouting";

const TAB_LABEL = {
  patient: "Patient Notes",
  evaluation: "Evaluation",
  daily: "Daily Notes",
};

function formatDate(iso) {
  const d = new Date(iso);
  return d.toLocaleDateString("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

function placeholderFor(label, tab) {
  // Generic, label-driven placeholder so it always matches the visible field name
  // regardless of therapy type.
  if (!label) return `Notes…`;
  return tab === "evaluation"
    ? `${label} — findings, history, plan, etc. Type or speak.`
    : `${label} — today's findings, response, progress. Type or speak.`;
}

function buildFields(cfg, tab) {
  const labels = tab === "evaluation" ? cfg.evaluation : cfg.daily;
  const slots = tab === "evaluation" ? EVAL_SLOTS : DAILY_SLOTS;
  return labels.map((label, i) => ({
    key: slots[i],
    label,
    placeholder: placeholderFor(label, tab),
    index: i,
  }));
}

export default function PlanEditor({
  patient,
  plan,
  onChange,
  onSave,
  onSubmit,
  onBack,
  onAudioCaptured,
  saveStatus = "idle",
  therapyType = "physiotherapy",
  showVoiceHint = false,
  onDismissVoiceHint,
  showDailyHint = false,
  onDismissDailyHint,
  forceWhisper = false,
}) {
  const [activeTab, setActiveTab] = useState("patient");
  const speech = useVoiceCapture();
  const [extracting, setExtracting] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [whisperAvailable, setWhisperAvailable] = useState(false);

  // Probe whether the backend has an OPENAI_API_KEY configured.
  useEffect(() => {
    let mounted = true;
    getTranscribeStatus()
      .then((r) => {
        if (mounted) setWhisperAvailable(!!r?.available);
      })
      .catch(() => {});
    return () => {
      mounted = false;
    };
  }, []);

  // A plan locks its therapy_type at creation time so historic plans keep
  // their original labels even after the therapist switches discipline.
  const effectiveTherapyType = plan?.therapy_type || therapyType;
  const cfg = useMemo(() => getTherapyConfig(effectiveTherapyType), [effectiveTherapyType]);
  const evalFields = useMemo(() => buildFields(cfg, "evaluation"), [cfg]);
  const dailyFields = useMemo(() => buildFields(cfg, "daily"), [cfg]);

  // Refs
  const planRef = useRef(plan);
  planRef.current = plan;
  const activeTabRef = useRef(activeTab);
  activeTabRef.current = activeTab;
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const prevListeningRef = useRef(false);
  const lastCommittedAudioRef = useRef(null);
  const whisperHandlerRef = useRef(null);

  // Stream slot transcripts live into the plan fields while listening
  useEffect(() => {
    if (!speech.listening) return;
    const tab = activeTabRef.current;
    if (tab === "patient") return; // patient tab has no slot routing
    const slots = speech.slotTranscripts || {};
    const current = planRef.current[tab] || {};
    const patch = { ...current };
    let changed = false;
    Object.keys(slots).forEach((k) => {
      if ((current[k] || "") !== (slots[k] || "")) {
        patch[k] = slots[k] || "";
        changed = true;
      }
    });
    if (changed) {
      onChangeRef.current({ ...planRef.current, [tab]: patch });
    }
  }, [speech.slotTranscripts, speech.listening]);

  // Commit transcript on stop (listening: true -> false)
  useEffect(() => {
    if (prevListeningRef.current && !speech.listening) {
      const tab = activeTabRef.current;
      const current = planRef.current[tab] || {};
      const newTranscript = speech.transcript || current.transcript || "";
      const patch = { ...current, transcript: newTranscript };
      if (tab !== "patient") {
        const slots = speech.slotTranscripts || {};
        Object.keys(slots).forEach((k) => {
          if (slots[k]) patch[k] = slots[k];
        });
      }
      onChangeRef.current({ ...planRef.current, [tab]: patch });
    }
    prevListeningRef.current = speech.listening;
  }, [speech.listening, speech.transcript, speech.slotTranscripts]);

  // Commit audio when MediaRecorder produces a blob (after stop)
  useEffect(() => {
    if (speech.audioUrl && speech.audioUrl !== lastCommittedAudioRef.current) {
      lastCommittedAudioRef.current = speech.audioUrl;
      const tab = activeTabRef.current;
      const current = planRef.current[tab];
      onChangeRef.current({
        ...planRef.current,
        [tab]: { ...current, audioUrl: speech.audioUrl },
      });
      if (onAudioCaptured && speech.audioBlob) {
        onAudioCaptured(tab, speech.audioBlob);
      }
      // Auto-chain Whisper → Claude on stop. Runs for the public Demo
      // (forceWhisper) AND for logged-in Trial/Solo users whenever the backend
      // has Whisper available. Therapist gets structured clinical fields
      // without an extra click. Defer to next tick so React can flush the
      // audioUrl state update first (otherwise the handler closes over a
      // stale plan and overwrites the section without audioUrl → the
      // AudioPlayer + tab dot briefly disappear).
      if (whisperAvailable && whisperHandlerRef.current) {
        setTimeout(() => whisperHandlerRef.current && whisperHandlerRef.current(), 0);
      }
    }
  }, [speech.audioUrl, speech.audioBlob, onAudioCaptured, forceWhisper, whisperAvailable]);

  const updateField = (tabType, key, value) => {
    onChange({
      ...plan,
      [tabType]: { ...plan[tabType], [key]: value },
    });
  };

  const handleTabChange = (newTab) => {
    if (speech.listening) speech.stop();
    speech.reset();
    lastCommittedAudioRef.current = null;
    setActiveTab(newTab);
  };

  const buildRoutingForTab = (tab) => {
    if (tab === "patient") return null;
    const fields = tab === "evaluation" ? evalFields : dailyFields;
    return {
      slots: fields.map((f) => ({ key: f.key, label: f.label })),
      defaultSlotKey: fields[0]?.key,
      initialSlotKey: fields[0]?.key,
    };
  };

  const handleToggleMic = () => {
    if (speech.listening) {
      speech.stop();
    } else {
      const routing = buildRoutingForTab(activeTab);
      speech.start(plan[activeTab].transcript || "", {
        routing,
        skipSpeechRecognition: forceWhisper,
      });
    }
  };

  // Record-only mode = no live Web Speech (Firefox / Safari iOS),
  // OR caller explicitly forced Whisper (e.g. the public demo, for A/B
  // testing transcription quality).
  const recordOnlyMode = !speech.supported || forceWhisper;

  const openInNewTab = () => {
    if (typeof window !== "undefined") {
      window.open(window.location.href, "_blank", "noopener");
    }
  };

  const handleExtract = async () => {
    const transcript = (
      speech.listening ? speech.transcript : plan[activeTab].transcript || ""
    ).trim();
    if (!transcript) {
      toast.error("Nothing to extract yet", {
        description: "Tap the mic and record some speech first.",
      });
      return;
    }
    if (speech.listening) speech.stop();

    setExtracting(true);
    try {
      const result = await extractClinicalNotes(
        transcript,
        activeTab,
        (n, max) =>
          toast.message(`Reconnecting… (${n}/${max - 1})`, {
            description: "The server is briefly waking up. Hold on a moment.",
          }),
        effectiveTherapyType
      );

      if (activeTab === "patient") {
        onChange({
          ...plan,
          patient: {
            ...plan.patient,
            transcript: result.raw || transcript,
            summary: result.summary || "",
          },
        });
        toast.success("Patient narrative summarised", {
          description: "Raw transcript and AI summary both saved.",
        });
      } else {
        const merged = { ...plan[activeTab] };
        Object.keys(result).forEach((k) => {
          if (result[k]) merged[k] = result[k];
        });
        onChange({ ...plan, [activeTab]: merged });
        toast.success("Fields auto-filled", {
          description: "Review the AI-extracted fields — edit any of them.",
        });
      }
    } catch (e) {
      toast.error("Extraction failed", {
        description: e?.message || "Please try again.",
      });
    } finally {
      setExtracting(false);
    }
  };

  const buildEmailBody = () => {
    const lines = [];
    lines.push(`Patient: ${patient.id} (${patient.initials})`);
    lines.push(`Date: ${formatDate(plan.date)}`);
    lines.push(`Discipline: ${cfg.label}`);
    lines.push("");
    lines.push("=== PATIENT NOTES ===");
    lines.push(`Patient (raw transcript):\n${plan.patient?.transcript || "(blank)"}\n`);
    lines.push(`AI summary:\n${plan.patient?.summary || "(blank)"}\n`);
    lines.push("=== EVALUATION ===");
    evalFields.forEach((f) => {
      lines.push(
        `${f.label}:\n${readSlot(plan.evaluation, f.key, f.index, "evaluation") || "(blank)"}\n`
      );
    });
    lines.push("=== DAILY NOTES ===");
    dailyFields.forEach((f) => {
      lines.push(
        `${f.label}:\n${readSlot(plan.daily, f.key, f.index, "daily") || "(blank)"}\n`
      );
    });
    return lines.join("\n");
  };

  const handleEmail = () => {
    const subject = `TherapyNote — ${patient.id} — ${formatDate(plan.date)}`;
    const body = buildEmailBody();
    const url = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.location.href = url;
  };

  const handleSubmit = async () => {
    if (!plan?.id) return;
    if (submitting) return;
    setSubmitting(true);
    try {
      // Flush in-progress edits first
      await onSave?.(plan, /* silent */ true);
      const updated = await submitPlan(plan.id);
      onChange({ ...plan, status: updated.status, submitted_at: updated.submitted_at });
      toast.success("Plan submitted", {
        description: "Marked as finalised. You can keep editing if needed.",
      });
    } catch (e) {
      toast.error("Couldn't submit", { description: e.message });
    } finally {
      setSubmitting(false);
    }
  };

  /**
   * Re-transcribe the current tab's captured audio using OpenAI Whisper, and
   * route the result back into the same fields. We always have a fresh blob
   * for the current tab inside the `speech` hook (until the user navigates).
   * Falls back gracefully if the backend isn't configured.
   */
  const handleWhisperRetranscribe = async () => {
    if (!speech.audioBlob) {
      toast.error("No recording on this tab", {
        description: "Record some audio first, then re-transcribe with AI.",
      });
      return;
    }
    setTranscribing(true);
    try {
      const result = await transcribeAudio(speech.audioBlob, {
        plan_id: planRef.current?.id,
        tab: activeTabRef.current,
        demo: forceWhisper,
      });
      const text = (result?.text || "").trim();
      if (!text) {
        toast.error("Whisper returned an empty transcript");
        return;
      }
      // Use planRef.current so we always merge on top of the freshest plan —
      // this preserves audioUrl / audio_path that the audio-commit effect
      // wrote moments earlier (otherwise the AudioPlayer + tab dot would
      // disappear as soon as auto-chain overwrites the section).
      const tab = activeTabRef.current;
      const current = planRef.current[tab] || {};
      const patch = { ...current, transcript: text };
      if (tab !== "patient") {
        const routing = buildRoutingForTab(tab);
        const slots = routeTranscriptToSlots(text, routing);
        Object.assign(patch, slots);
      }
      onChange({ ...planRef.current, [tab]: patch });
      toast.success("Transcribed with Whisper", {
        description: "Transcript ready. Filling clinical fields…",
      });

      // Option B: auto-chain Claude extraction (Demo + Trial + Solo) so the
      // therapist gets structured fields without a second click.
      try {
        setExtracting(true);
        const extracted = await extractClinicalNotes(
          text,
          tab,
          null,
          effectiveTherapyType
        );
        if (tab === "patient") {
          const cur = planRef.current.patient || {};
          onChange({
            ...planRef.current,
            patient: {
              ...cur,
              transcript: extracted.raw || text,
              summary: extracted.summary || "",
            },
          });
        } else {
          const cur = planRef.current[tab] || {};
          const merged = { ...cur };
          Object.keys(extracted).forEach((k) => {
            if (extracted[k]) merged[k] = extracted[k];
          });
          onChange({ ...planRef.current, [tab]: merged });
        }
        toast.success("Clinical fields filled", {
          description: "Review or edit any field below.",
        });
      } catch (e) {
        toast.error("Field extraction failed", { description: e.message });
      } finally {
        setExtracting(false);
      }
    } catch (e) {
      toast.error("Re-transcribe failed", { description: e.message });
    } finally {
      setTranscribing(false);
    }
  };
  whisperHandlerRef.current = handleWhisperRetranscribe;

  // What to show in the transcript textarea: live while listening, persisted otherwise.
  const displayedTranscript = speech.listening
    ? speech.transcript
    : plan[activeTab]?.transcript || "";

  const tabAudioUrl = plan[activeTab]?.audioUrl || null;
  const extractCtaLabel = activeTab === "patient" ? "Summarise with AI" : "Extract with AI";

  // Build the spoken-cues hint for current tab
  const cueLabels = useMemo(() => {
    if (activeTab === "evaluation") return evalFields.map((f) => f.label);
    if (activeTab === "daily") return dailyFields.map((f) => f.label);
    return [];
  }, [activeTab, evalFields, dailyFields]);

  const transcriptPlaceholder = transcribing
    ? "Transcribing your recording with OpenAI Whisper…"
    : recordOnlyMode
    ? "Tap the mic, speak naturally, then tap stop. Whisper will transcribe and route your words into the fields above."
    : activeTab === "patient"
    ? "Tap the mic and let the patient speak. Their words appear here — editable, and saved when you stop."
    : `Tap the mic and dictate. Say a field name (${cueLabels.join(
        ", "
      )}) and the next words will route into that field.`;

  return (
    <div data-testid="plan-editor" className="space-y-5">
      {/* Top bar */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <button
          onClick={onBack}
          data-testid="plan-editor-back-btn"
          className="inline-flex items-center gap-2 font-figtree text-sm text-[#475569] hover:text-[#0F172A] transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to patient
        </button>
        <div className="flex items-center gap-3 flex-wrap">
          <div
            data-testid="plan-editor-save-status"
            className={`inline-flex items-center gap-1.5 font-figtree text-xs font-medium transition-colors ${
              saveStatus === "saving"
                ? "text-[#94A3B8]"
                : saveStatus === "saved"
                ? "text-[#059669]"
                : saveStatus === "error"
                ? "text-[#DC2626]"
                : "text-transparent"
            }`}
          >
            {saveStatus === "saving" && (
              <>
                <Loader2 className="w-3 h-3 animate-spin" />
                Saving…
              </>
            )}
            {saveStatus === "saved" && (
              <>
                <span className="w-1.5 h-1.5 rounded-full bg-[#10B981]" />
                All changes saved
              </>
            )}
            {saveStatus === "error" && <>Save failed — retrying…</>}
          </div>
          <Button
            data-testid="plan-editor-email-btn"
            onClick={handleEmail}
            variant="outline"
            className="rounded-full border-slate-200 hover:bg-slate-50 font-figtree text-sm"
          >
            <Mail className="w-4 h-4 mr-2" />
            Email me this plan
          </Button>
          <Button
            data-testid="plan-editor-save-btn"
            onClick={() => onSave(plan)}
            className="rounded-full bg-[#0F172A] hover:bg-[#1E293B] text-white font-figtree text-sm font-medium px-6"
          >
            <Save className="w-4 h-4 mr-2" />
            Save plan
          </Button>
          <Button
            data-testid="plan-editor-submit-btn"
            onClick={handleSubmit}
            disabled={submitting || plan.status === "submitted"}
            className="rounded-full bg-[#10B981] hover:bg-[#059669] text-white font-figtree text-sm font-medium px-6 disabled:opacity-60"
          >
            {submitting ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Submitting…
              </>
            ) : plan.status === "submitted" ? (
              <>
                <CheckCircle2 className="w-4 h-4 mr-2" />
                Submitted
              </>
            ) : (
              <>
                <Send className="w-4 h-4 mr-2" />
                Submit plan
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Patient strip */}
      <div className="bg-white rounded-2xl border border-slate-200/80 shadow-soft p-4 flex items-center gap-4">
        <span className="inline-flex items-center justify-center w-11 h-11 rounded-xl bg-[#D1FAE5] text-[#065F46] font-outfit text-sm font-bold">
          {patient.initials}
        </span>
        <div className="flex-1 min-w-0">
          <div className="font-outfit text-base font-semibold text-[#0F172A]">
            {patient.id}
          </div>
          <div className="font-figtree text-xs text-[#64748B] truncate">
            {patient.initials} · {formatDate(plan.date)} · {cfg.label}
          </div>
        </div>
        <span
          data-testid="plan-status-badge"
          className={`inline-flex items-center px-2.5 py-1 rounded-full text-[10px] font-semibold uppercase tracking-wider ${
            plan.status === "submitted"
              ? "bg-[#D1FAE5] text-[#065F46]"
              : "bg-[#FEF3C7] text-[#92400E]"
          }`}
        >
          {plan.status === "submitted" ? "Submitted" : "Draft"}
        </span>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
        <TabsList
          data-testid="plan-editor-tabs"
          className="bg-white border border-slate-200/80 rounded-full p-1 h-auto shadow-soft"
        >
          <TabsTrigger
            value="patient"
            data-testid="plan-editor-tab-patient"
            className="rounded-full font-figtree text-sm px-5 py-2 data-[state=active]:bg-[#10B981] data-[state=active]:text-white data-[state=active]:shadow-[0_4px_12px_rgba(16,185,129,0.3)] transition-all"
          >
            Patient Notes
            {(plan.patient?.audio_path || plan.patient?.audioUrl) && (
              <span
                data-testid="tab-audio-dot-patient"
                title="Audio recorded"
                className="ml-2 inline-block w-1.5 h-1.5 rounded-full bg-[#10B981]"
              />
            )}
          </TabsTrigger>
          <TabsTrigger
            value="evaluation"
            data-testid="plan-editor-tab-evaluation"
            className="rounded-full font-figtree text-sm px-5 py-2 data-[state=active]:bg-[#10B981] data-[state=active]:text-white data-[state=active]:shadow-[0_4px_12px_rgba(16,185,129,0.3)] transition-all"
          >
            Evaluation
            {(plan.evaluation?.audio_path || plan.evaluation?.audioUrl) && (
              <span
                data-testid="tab-audio-dot-evaluation"
                title="Audio recorded"
                className="ml-2 inline-block w-1.5 h-1.5 rounded-full bg-[#10B981]"
              />
            )}
          </TabsTrigger>
          <TabsTrigger
            value="daily"
            data-testid="plan-editor-tab-daily"
            className="rounded-full font-figtree text-sm px-5 py-2 data-[state=active]:bg-[#10B981] data-[state=active]:text-white data-[state=active]:shadow-[0_4px_12px_rgba(16,185,129,0.3)] transition-all"
          >
            Daily Notes
            {(plan.daily?.audio_path || plan.daily?.audioUrl) && (
              <span
                data-testid="tab-audio-dot-daily"
                title="Audio recorded"
                className="ml-2 inline-block w-1.5 h-1.5 rounded-full bg-[#10B981]"
              />
            )}
          </TabsTrigger>
        </TabsList>

        {/* Voice + extract panel — common across tabs but bound to active tab */}
        <div className="mt-4 bg-white rounded-3xl border border-slate-200/80 shadow-soft p-5 md:p-6">
          <div className="flex flex-col md:flex-row gap-5 items-start">
            <button
              onClick={handleToggleMic}
              data-testid="plan-editor-mic-btn"
              className={`flex-shrink-0 w-16 h-16 md:w-20 md:h-20 rounded-full flex items-center justify-center text-white transition-all ${
                speech.listening
                  ? "bg-[#EF4444] mic-pulse"
                  : "bg-[#10B981] hover:bg-[#059669] shadow-[0_10px_25px_rgba(16,185,129,0.4)] hover:-translate-y-0.5"
              }`}
              aria-label={speech.listening ? "Stop recording" : "Start recording"}
            >
              {speech.listening ? (
                <MicOff className="w-7 h-7" />
              ) : (
                <Mic className="w-7 h-7" />
              )}
            </button>

            <div className="flex-1 w-full">
              <div className="flex items-center justify-between gap-3 mb-2">
                <div>
                  <div className="font-outfit text-sm font-semibold text-[#0F172A]">
                    {speech.listening
                      ? recordOnlyMode
                        ? `Recording — ${TAB_LABEL[activeTab]}`
                        : `Listening — ${TAB_LABEL[activeTab]}`
                      : `Tap the microphone — ${TAB_LABEL[activeTab]}`}
                  </div>
                  <div className="font-figtree text-xs text-[#64748B]">
                    {recordOnlyMode
                      ? forceWhisper
                        ? speech.listening
                          ? "Recording with OpenAI Whisper — tap stop, then Transcribe with AI to see the result."
                          : "Demo uses OpenAI Whisper for transcription. Record your session, then tap Transcribe with AI."
                        : speech.listening
                        ? "Your browser doesn't support live transcription. Recording audio now — tap Transcribe with AI when you're done."
                        : "Your browser doesn't support live transcription. Record your session, then tap Transcribe with AI to fill the fields."
                      : activeTab === "patient"
                      ? "Patient speaks. Their words are saved as the patient narrative."
                      : speech.listening && speech.activeSlot
                      ? `Routing to: ${
                          cueLabels[
                            (activeTab === "evaluation"
                              ? evalFields
                              : dailyFields
                            ).findIndex((f) => f.key === speech.activeSlot)
                          ] || cueLabels[0]
                        }`
                      : "Say a section name (e.g. " +
                        cueLabels.slice(0, 2).join(", ") +
                        ") and we'll route the next words into that field."}
                  </div>
                </div>
                {displayedTranscript && !speech.listening && (
                  <button
                    onClick={() => {
                      const clear = { transcript: "", audioUrl: null };
                      if (activeTab !== "patient") {
                        (activeTab === "evaluation" ? evalFields : dailyFields).forEach(
                          (f) => (clear[f.key] = "")
                        );
                      } else {
                        clear.summary = "";
                      }
                      onChange({
                        ...plan,
                        [activeTab]: { ...plan[activeTab], ...clear },
                      });
                      speech.reset();
                      lastCommittedAudioRef.current = null;
                    }}
                    data-testid="plan-editor-reset-transcript-btn"
                    className="inline-flex items-center gap-1.5 font-figtree text-xs text-[#64748B] hover:text-[#EF4444] transition-colors"
                  >
                    <RotateCcw className="w-3 h-3" />
                    Clear
                  </button>
                )}
              </div>

              <Textarea
                data-testid="plan-editor-transcript"
                value={displayedTranscript}
                readOnly={speech.listening}
                onChange={(e) =>
                  updateField(activeTab, "transcript", e.target.value)
                }
                placeholder={transcriptPlaceholder}
                rows={5}
                className="font-figtree text-sm bg-[#F8FAFC] border-slate-200 focus-visible:ring-[#10B981] rounded-xl resize-none"
              />

              {/* Field cue chips for non-patient tabs */}
              {activeTab !== "patient" && cueLabels.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1.5" data-testid="voice-cues">
                  {(activeTab === "evaluation" ? evalFields : dailyFields).map((f) => {
                    const active = speech.listening && speech.activeSlot === f.key;
                    return (
                      <span
                        key={f.key}
                        data-testid={`voice-cue-${f.key}`}
                        className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-figtree font-medium transition-colors ${
                          active
                            ? "bg-[#10B981] text-white"
                            : "bg-[#F1F5F9] text-[#475569]"
                        }`}
                      >
                        {active && (
                          <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
                        )}
                        {f.label}
                      </span>
                    );
                  })}
                </div>
              )}

              {tabAudioUrl && !speech.listening && (
                <AudioPlayer
                  src={tabAudioUrl}
                  label={`${TAB_LABEL[activeTab]} audio`}
                  mode={forceWhisper ? "demo" : "saved"}
                />
              )}

              <div className="mt-3 flex flex-col sm:flex-row gap-2 sm:justify-end">
                {!forceWhisper && whisperAvailable && speech.audioBlob && !speech.listening && (
                  <Button
                    data-testid="plan-editor-whisper-btn"
                    onClick={handleWhisperRetranscribe}
                    disabled={transcribing || extracting}
                    variant={recordOnlyMode ? "default" : "outline"}
                    className={
                      recordOnlyMode
                        ? "rounded-full bg-[#10B981] hover:bg-[#059669] text-white font-figtree text-sm font-medium px-5 disabled:opacity-50"
                        : "rounded-full border-[#A7F3D0] text-[#065F46] hover:bg-[#ECFDF5] font-figtree text-sm font-medium px-5 disabled:opacity-50"
                    }
                    title={
                      recordOnlyMode
                        ? "Send the recording to OpenAI Whisper to transcribe it"
                        : "Replace the live transcript with a Whisper transcription of the recording"
                    }
                  >
                    {transcribing ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Transcribing…
                      </>
                    ) : (
                      <>
                        <Wand2 className="w-4 h-4 mr-2" />
                        {recordOnlyMode ? "Transcribe with AI" : "Re-transcribe with AI"}
                      </>
                    )}
                  </Button>
                )}
                {!forceWhisper && (
                  <Button
                    onClick={handleExtract}
                    data-testid="plan-editor-extract-btn"
                    disabled={extracting || !displayedTranscript.trim()}
                    className="rounded-full bg-[#064E3B] hover:bg-[#065F46] text-white font-figtree text-sm font-medium px-5 disabled:opacity-50"
                  >
                    {extracting ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        {activeTab === "patient" ? "Summarising…" : "Extracting…"}
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-4 h-4 mr-2" />
                        {extractCtaLabel}
                      </>
                    )}
                  </Button>
                )}
                {forceWhisper && (transcribing || extracting) && (
                  <div
                    data-testid="plan-editor-autochain-status"
                    className="inline-flex items-center gap-2 rounded-full bg-[#ECFDF5] border border-[#A7F3D0] text-[#065F46] font-figtree text-xs font-medium px-4 py-2"
                  >
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    {transcribing ? "Transcribing…" : "Filling clinical fields…"}
                  </div>
                )}
              </div>

              {speech.error && (
                <div
                  data-testid="plan-editor-mic-error"
                  className="mt-3 rounded-2xl border border-[#FCA5A5] bg-[#FEF2F2] p-4"
                >
                  <div className="flex items-start gap-3">
                    <span className="flex-shrink-0 inline-flex items-center justify-center w-7 h-7 rounded-full bg-[#FECACA] text-[#B91C1C]">
                      <MicOff className="w-3.5 h-3.5" />
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="font-outfit text-sm font-semibold text-[#7F1D1D]">
                        {speech.errorCode === "not-allowed"
                          ? "Microphone access blocked"
                          : "Voice dictation problem"}
                      </div>
                      <p className="mt-1 font-figtree text-xs text-[#991B1B] leading-relaxed">
                        {speech.error}
                      </p>
                      {speech.errorCode === "not-allowed" && speech.inIframe && (
                        <p className="mt-2 font-figtree text-xs text-[#991B1B] leading-relaxed">
                          Looks like you're viewing this inside an embedded
                          preview, which often blocks the microphone. Opening
                          the app in its own tab usually fixes it.
                        </p>
                      )}
                      {speech.errorCode === "not-allowed" && !speech.inIframe && (
                        <ol className="mt-2 list-decimal pl-4 font-figtree text-xs text-[#991B1B] space-y-0.5">
                          <li>Click the lock or tune icon left of the URL.</li>
                          <li>
                            Set <strong>Microphone</strong> to{" "}
                            <strong>Allow</strong>.
                          </li>
                          <li>Reload the page and tap the mic again.</li>
                        </ol>
                      )}
                      <div className="mt-3 flex flex-wrap gap-2">
                        <button
                          data-testid="plan-editor-mic-retry-btn"
                          onClick={() => {
                            speech.dismissError();
                            const routing = buildRoutingForTab(activeTab);
                            speech.start(plan[activeTab].transcript || "", {
                              routing,
                              skipSpeechRecognition: forceWhisper,
                            });
                          }}
                          className="rounded-full bg-[#7F1D1D] hover:bg-[#991B1B] text-white font-figtree text-xs font-medium px-4 py-1.5 transition-colors"
                        >
                          Try again
                        </button>
                        {speech.inIframe && (
                          <button
                            data-testid="plan-editor-open-tab-btn"
                            onClick={openInNewTab}
                            className="rounded-full bg-white border border-[#FCA5A5] hover:bg-[#FEF2F2] text-[#991B1B] font-figtree text-xs font-medium px-4 py-1.5 transition-colors"
                          >
                            Open in a new tab
                          </button>
                        )}
                        <button
                          data-testid="plan-editor-mic-dismiss-btn"
                          onClick={speech.dismissError}
                          className="rounded-full text-[#991B1B] hover:bg-[#FECACA] font-figtree text-xs font-medium px-3 py-1.5 transition-colors"
                        >
                          Dismiss
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Tab content — fields below the voice panel */}
        <TabsContent value="patient" className="mt-5 space-y-4">
          <FieldEditor
            label="Patient narrative (raw)"
            placeholder="The patient's own words will appear here once they speak. Editable."
            value={plan.patient?.transcript || ""}
            onChange={(v) => updateField("patient", "transcript", v)}
            testIdBase="patient-transcript"
          />
          <FieldEditor
            label="AI summary"
            placeholder="A concise, well-structured patient narrative will appear here after you tap Summarise with AI."
            value={plan.patient?.summary || ""}
            onChange={(v) => updateField("patient", "summary", v)}
            testIdBase="patient-summary"
          />
        </TabsContent>

        <TabsContent value="evaluation" className="mt-5 space-y-4">
          {showVoiceHint && (
            <VoiceRoutingHint
              labels={evalFields.map((f) => f.label)}
              onDismiss={onDismissVoiceHint}
            />
          )}
          {evalFields.map((f) => (
            <FieldEditor
              key={f.key}
              label={f.label}
              placeholder={f.placeholder}
              value={readSlot(plan.evaluation, f.key, f.index, "evaluation")}
              onChange={(v) => updateField("evaluation", f.key, v)}
              testIdBase={`eval-${f.key}`}
            />
          ))}
        </TabsContent>

        <TabsContent value="daily" className="mt-5 space-y-4">
          {showDailyHint && (
            <VoiceRoutingHint
              labels={dailyFields.map((f) => f.label)}
              onDismiss={onDismissDailyHint}
              tabName="Daily Notes"
              testIdPrefix="daily-voice-routing-hint"
            />
          )}
          {dailyFields.map((f) => (
            <FieldEditor
              key={f.key}
              label={f.label}
              placeholder={f.placeholder}
              value={readSlot(plan.daily, f.key, f.index, "daily")}
              onChange={(v) => updateField("daily", f.key, v)}
              testIdBase={`daily-${f.key}`}
            />
          ))}
        </TabsContent>
      </Tabs>
    </div>
  );
}
